import { useState } from 'react'
import { ChevronDown, ChevronUp, Lightbulb, CheckCircle2, HelpCircle, Sparkles, ListTodo, Loader } from 'lucide-react'
import { findGuide } from './sectionGuides'
import { supabase } from '../../lib/supabase'

/**
 * CajaGuidePanel — contextual guidance sidebar for Consultoría en una Caja.
 * Shown at the top of SectionPage when the user is a caja_user.
 *
 * Props:
 *   sectionName     : string  — raw section name from DB
 *   sectionId       : string  — section UUID (for auto-task creation)
 *   consultancyId   : string  — consultancy UUID
 *   existingTasks   : array   — tasks already in DB (to avoid duplication)
 *   onTasksCreated  : fn()    — callback to reload tasks after creation
 */
export default function CajaGuidePanel({ sectionName, sectionId, consultancyId, existingTasks = [], onTasksCreated }) {
  const guide = findGuide(sectionName)
  const [open, setOpen]           = useState(true)
  const [tab, setTab]             = useState('steps')   // 'steps' | 'questions' | 'tip'
  const [creating, setCreating]   = useState(false)
  const [created, setCreated]     = useState(false)

  if (!guide) return null

  const hasTasks = existingTasks.length > 0

  async function handleCreateTasks() {
    if (creating || hasTasks || !guide.tasks?.length) return
    setCreating(true)
    try {
      const rows = guide.tasks.map((t, idx) => ({
        section_id:    sectionId,
        consultancy_id: consultancyId,
        name:          t.name,
        priority:      t.priority ?? 'medium',
        status:        'pending',
        order:         idx,
      }))
      await supabase.from('tasks').insert(rows)
      setCreated(true)
      onTasksCreated?.()
    } catch (err) {
      console.error('Error creating guided tasks:', err)
    } finally {
      setCreating(false)
    }
  }

  const gradientClass = guide.color ?? 'from-brand-600 to-brand-700'

  return (
    <div className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden bg-white">
      {/* Header — always visible */}
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center gap-3 px-5 py-4 bg-gradient-to-r ${gradientClass} text-white text-left`}
      >
        <span className="text-2xl leading-none flex-shrink-0">{guide.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-xs font-bold uppercase tracking-wide opacity-80">Guía de sección</span>
          </div>
          <p className="text-sm font-semibold leading-snug line-clamp-1">{guide.what}</p>
        </div>
        <div className="flex-shrink-0 opacity-70">
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </button>

      {open && (
        <div className="px-5 py-4 space-y-4">

          {/* Goal */}
          <div className="flex items-start gap-2.5 bg-gray-50 rounded-xl px-4 py-3">
            <CheckCircle2 size={15} className="text-emerald-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Resultado esperado</p>
              <p className="text-sm text-gray-700 leading-relaxed">{guide.goal}</p>
            </div>
          </div>

          {/* Tab switcher */}
          <div className="flex gap-1 border-b border-gray-100">
            {[
              { id: 'steps',     label: 'Pasos',      Icon: ListTodo   },
              { id: 'questions', label: 'Preguntas',   Icon: HelpCircle },
              { id: 'tip',       label: 'Consejo',     Icon: Lightbulb  },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 transition-all ${
                  tab === id
                    ? 'border-brand-600 text-brand-700'
                    : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}
              >
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>

          {/* Steps tab */}
          {tab === 'steps' && (
            <ol className="space-y-2">
              {guide.steps.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-50 text-brand-700 text-xs font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-sm text-gray-700 leading-relaxed pt-0.5">{step}</p>
                </li>
              ))}
            </ol>
          )}

          {/* Questions tab */}
          {tab === 'questions' && (
            <ul className="space-y-2">
              {guide.questions.map((q, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="flex-shrink-0 text-brand-400 mt-0.5">›</span>
                  <p className="text-sm text-gray-700 leading-relaxed">{q}</p>
                </li>
              ))}
            </ul>
          )}

          {/* Tip tab */}
          {tab === 'tip' && (
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
              <Lightbulb size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700 leading-relaxed">{guide.tip}</p>
            </div>
          )}

          {/* Auto-tasks CTA */}
          {guide.tasks?.length > 0 && (
            <div className="pt-1">
              {hasTasks || created ? (
                <div className="flex items-center gap-2 text-xs text-emerald-600 font-medium">
                  <CheckCircle2 size={14} />
                  {created ? 'Tareas creadas correctamente' : 'Las tareas guiadas ya están en la sección'}
                </div>
              ) : (
                <button
                  onClick={handleCreateTasks}
                  disabled={creating}
                  className="w-full flex items-center justify-center gap-2 bg-brand-700 hover:bg-brand-800 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"
                >
                  {creating
                    ? <><Loader size={14} className="animate-spin" /> Creando tareas…</>
                    : <><Sparkles size={14} /> Agregar {guide.tasks.length} tareas sugeridas</>
                  }
                </button>
              )}
            </div>
          )}

        </div>
      )}
    </div>
  )
}
